import { useEffect, useState, FormEvent } from 'react';
import { Plus, Pencil, Check, X, AlertCircle, Layers, Power } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Product, Workflow } from '@/types';

// ─── Colour picker swatches ───────────────────────────────────────────────────

const SWATCHES = [
  '#6366F1', '#8B5CF6', '#EC4899', '#EF4444',
  '#F59E0B', '#10B981', '#06B6D4', '#3B82F6',
  '#64748B', '#1F2937',
];

// ─── Inline editable product row ─────────────────────────────────────────────

function ProductRow({
  product,
  workflows,
  onSaved,
}: {
  product: Product;
  workflows: Workflow[];
  onSaved: (updated: Product) => void;
}) {
  const [editing, setEditing]       = useState(false);
  const [name, setName]             = useState(product.name);
  const [color, setColor]           = useState(product.color);
  const [selectedWf, setSelectedWf] = useState<number[]>(product.workflow_ids ?? []);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);

  function resetForm() {
    setName(product.name);
    setColor(product.color);
    setSelectedWf(product.workflow_ids ?? []);
    setError(null);
    setEditing(false);
  }

  async function save() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const updated = await api.updateProduct(product.id, {
        name: name.trim(),
        color,
        workflow_ids: selectedWf,
      });
      onSaved(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    setSaving(true);
    try {
      if (product.is_active) {
        await api.deactivateProduct(product.id);
        onSaved({ ...product, is_active: 0 });
      } else {
        const updated = await api.updateProduct(product.id, { is_active: true });
        onSaved(updated);
      }
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to toggle.');
    } finally {
      setSaving(false);
    }
  }

  function toggleWf(id: number) {
    setSelectedWf((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border ${product.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-dashed border-gray-300 dark:border-gray-600 opacity-60'} p-4 transition-all`}>
      {error && (
        <div className="flex items-center gap-2 mb-3 text-xs text-red-600 dark:text-red-400">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        {/* Color dot */}
        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: product.color }} />

        {/* Name */}
        {editing ? (
          <input
            className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-400"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus
          />
        ) : (
          <span className="flex-1 font-medium text-gray-900 dark:text-white text-sm">{product.name}</span>
        )}

        {/* Actions */}
        {editing ? (
          <div className="flex items-center gap-1.5">
            <button onClick={save} disabled={saving} className="p-1.5 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">
              <Check size={13} />
            </button>
            <button onClick={resetForm} disabled={saving} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700">
              <X size={13} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700" title="Edit">
              <Pencil size={13} />
            </button>
            <button
              onClick={toggleActive}
              disabled={saving}
              className={`p-1.5 rounded-lg border text-xs disabled:opacity-50 ${product.is_active ? 'border-red-200 dark:border-red-800 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30' : 'border-green-200 dark:border-green-800 text-green-500 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30'}`}
              title={product.is_active ? 'Deactivate' : 'Re-activate'}
            >
              <Power size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Slug */}
      <div className="mt-1 ml-7 text-[11px] text-gray-400 dark:text-gray-500 font-mono">{product.slug}</div>

      {/* Colour picker (edit mode) */}
      {editing && (
        <div className="mt-3 ml-7">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Colour</div>
          <div className="flex gap-1.5 flex-wrap">
            {SWATCHES.map((s) => (
              <button
                key={s}
                onClick={() => setColor(s)}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${color === s ? 'border-gray-900 dark:border-white scale-125' : 'border-transparent'}`}
                style={{ background: s }}
                title={s}
              />
            ))}
          </div>
        </div>
      )}

      {/* Workflow mappings */}
      {editing && workflows.length > 0 && (
        <div className="mt-3 ml-7">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
            Mapped KB Workflows <span className="text-gray-400">(chat will only use these for this product)</span>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {workflows.map((w) => {
              const selected = selectedWf.includes(w.id!);
              return (
                <button
                  key={w.id}
                  onClick={() => toggleWf(w.id!)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    selected
                      ? 'bg-brand-600 border-brand-600 text-white'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-brand-400'
                  }`}
                >
                  {w.title}
                </button>
              );
            })}
          </div>
          {selectedWf.length > 0 && (
            <div className="mt-1 text-[11px] text-brand-600 dark:text-brand-400">
              {selectedWf.length} workflow{selectedWf.length !== 1 ? 's' : ''} mapped
            </div>
          )}
        </div>
      )}

      {/* Workflow count badge (view mode) */}
      {!editing && (
        <div className="mt-2 ml-7 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
          <Layers size={11} />
          {(product.workflow_ids ?? []).length === 0
            ? 'No workflows mapped (all workflows used)'
            : `${product.workflow_ids.length} workflow${product.workflow_ids.length !== 1 ? 's' : ''} mapped`}
        </div>
      )}
    </div>
  );
}

// ─── Add product form ─────────────────────────────────────────────────────────

function AddProductForm({
  workflows,
  onCreated,
}: {
  workflows: Workflow[];
  onCreated: (p: Product) => void;
}) {
  const [open, setOpen]             = useState(false);
  const [name, setName]             = useState('');
  const [color, setColor]           = useState('#6366F1');
  const [selectedWf, setSelectedWf] = useState<number[]>([]);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const p = await api.createProduct({ name: name.trim(), color, workflow_ids: selectedWf });
      onCreated(p);
      setName(''); setColor('#6366F1'); setSelectedWf([]); setOpen(false);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to create product.');
    } finally {
      setSaving(false);
    }
  }

  function toggleWf(id: number) {
    setSelectedWf((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
      >
        <Plus size={16} /> Add Product
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl border border-brand-300 dark:border-brand-600 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">New Product</h3>

      {error && (
        <div className="flex items-center gap-2 mb-3 text-xs text-red-600 dark:text-red-400">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      <div className="flex gap-3 items-center mb-3">
        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: color }} />
        <input
          className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-400"
          placeholder="Product name, e.g. ProMed5"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus
        />
      </div>

      <div className="mb-3">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Colour</div>
        <div className="flex gap-1.5 flex-wrap">
          {SWATCHES.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => setColor(s)}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${color === s ? 'border-gray-900 dark:border-white scale-125' : 'border-transparent'}`}
              style={{ background: s }}
            />
          ))}
        </div>
      </div>

      {workflows.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
            Map KB Workflows <span className="text-gray-400">(leave empty to use all)</span>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {workflows.map((w) => {
              const selected = selectedWf.includes(w.id!);
              return (
                <button
                  type="button"
                  key={w.id}
                  onClick={() => toggleWf(w.id!)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-brand-400'}`}
                >
                  {w.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          className="px-4 py-1.5 border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function Products() {
  const [products, setProducts]   = useState<Product[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api.listProducts(), api.listWorkflows('published')])
      .then(([prods, wfs]) => { setProducts(prods); setWorkflows(wfs); })
      .catch((e) => setError(e instanceof ApiClientError ? e.message : 'Failed to load.'))
      .finally(() => setLoading(false));
  }, []);

  function handleCreated(p: Product) {
    setProducts((prev) => [...prev, p].sort((a, b) => a.name.localeCompare(b.name)));
  }

  function handleSaved(updated: Product) {
    setProducts((prev) => prev.map((p) => p.id === updated.id ? updated : p));
  }

  const active   = products.filter((p) => p.is_active);
  const inactive = products.filter((p) => !p.is_active);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Products</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Manage products and map them to KB workflows for the chat widget.
          </p>
        </div>
        <AddProductForm workflows={workflows} onCreated={handleCreated} />
      </div>

      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center">Loading…</div>}

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && products.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-16 text-center">
          <Layers size={36} className="text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No products yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Add your first product to get started.</p>
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-3 mb-6">
          {active.map((p) => (
            <ProductRow key={p.id} product={p} workflows={workflows} onSaved={handleSaved} />
          ))}
        </div>
      )}

      {inactive.length > 0 && (
        <>
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2 mt-4">
            Deactivated
          </div>
          <div className="space-y-3">
            {inactive.map((p) => (
              <ProductRow key={p.id} product={p} workflows={workflows} onSaved={handleSaved} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
